/**
 * Utility functions for timezone conversions and validation
 */

/**
 * Normalizes a time string to strict 24-hour "HH:MM" form.
 * Accepts inputs like "7:00 AM", "07:00", "7:00:00 PM", "13:5".
 * Returns null if the input can't be parsed.
 */
const normalizeTime = (timeStr: string): string | null => {
    if (!timeStr) return null;
    const trimmed = timeStr.trim();

    // 12-hour with AM/PM (e.g. "7:00 AM", "12:30:00 pm")
    const ampmMatch = new RegExp(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i).exec(trimmed);
    if (ampmMatch) {
        let h = Number.parseInt(ampmMatch[1], 10);
        const m = Number.parseInt(ampmMatch[2], 10);
        const period = ampmMatch[3].toUpperCase();
        if (Number.isNaN(h) || Number.isNaN(m) || h < 1 || h > 12 || m < 0 || m > 59) return null;
        if (period === 'PM' && h !== 12) h += 12;
        if (period === 'AM' && h === 12) h = 0;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }

    // 24-hour (e.g. "07:00", "7:00", "13:05:00")
    const h24Match = new RegExp(/^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/).exec(trimmed);
    if (h24Match) {
        const h = Number.parseInt(h24Match[1], 10);
        const m = Number.parseInt(h24Match[2], 10);
        if (Number.isNaN(h) || Number.isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return null;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }

    return null;
};

/**
 * Maps common US timezone abbreviations AND Windows timezone display names
 * (as returned by the .NET TimeZoneInfo.Id on the backend) to IANA identifiers
 * so downstream Intl APIs work on strict engines (e.g. iOS WebKit) that only
 * accept IANA ids. Ideally callers pass IANA ids directly; this is a defensive
 * fallback.
 */
const TIMEZONE_ABBREVIATION_MAP: Record<string, string> = {
    // Short abbreviations
    EST: 'America/New_York',
    EDT: 'America/New_York',
    CST: 'America/Chicago',
    CDT: 'America/Chicago',
    MST: 'America/Denver',
    MDT: 'America/Denver',
    PST: 'America/Los_Angeles',
    PDT: 'America/Los_Angeles',
    AKST: 'America/Anchorage',
    AKDT: 'America/Anchorage',
    HST: 'Pacific/Honolulu',
    HAST: 'Pacific/Honolulu',
    HADT: 'America/Adak',

    // Windows timezone display names (case-insensitive lookup handled below)
    'EASTERN STANDARD TIME': 'America/New_York',
    'CENTRAL STANDARD TIME': 'America/Chicago',
    'MOUNTAIN STANDARD TIME': 'America/Denver',
    'US MOUNTAIN STANDARD TIME': 'America/Phoenix', // Arizona, no DST
    'PACIFIC STANDARD TIME': 'America/Los_Angeles',
    'ALASKAN STANDARD TIME': 'America/Anchorage',
    'HAWAIIAN STANDARD TIME': 'Pacific/Honolulu',
    'ATLANTIC STANDARD TIME': 'America/Halifax',
};

const resolveTimeZoneId = (timeZoneId: string): string => {
    if (!timeZoneId) return timeZoneId;
    const upper = timeZoneId.toUpperCase();
    return TIMEZONE_ABBREVIATION_MAP[upper] ?? timeZoneId;
};

/**
 * Returns the offset in minutes between UTC and the given IANA timezone at the
 * given instant. Uses Intl.DateTimeFormat.formatToParts, which is supported
 * consistently across engines (including iOS WebKit) unlike parsing the output
 * of Date.toLocaleString('en-US', ...).
 *
 * Positive value = timezone is ahead of UTC (e.g. Europe/Berlin => +60 or +120).
 * Negative value = timezone is behind UTC (e.g. America/Chicago => -300 or -360).
 */
const getTimezoneOffsetMinutes = (date: Date, timeZoneId: string): number => {
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: resolveTimeZoneId(timeZoneId),
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    const parts = dtf.formatToParts(date).reduce<Record<string, string>>((acc, p) => {
        if (p.type !== 'literal') acc[p.type] = p.value;
        return acc;
    }, {});
    // Intl may render midnight hour as "24"; normalize to 0.
    const hour = parts.hour === '24' ? 0 : Number.parseInt(parts.hour, 10);
    const asUTC = Date.UTC(
        Number.parseInt(parts.year, 10),
        Number.parseInt(parts.month, 10) - 1,
        Number.parseInt(parts.day, 10),
        hour,
        Number.parseInt(parts.minute, 10),
        Number.parseInt(parts.second, 10),
    );
    return (asUTC - date.getTime()) / 60000;
};

/**
 * Gets the current date and time in a specific timezone

 * @returns Date object representing current time in the specified timezone
 */
export const getCurrentTimeInTimezone = (timeZoneId: string): Date => {
    // Compute the wall-clock time in the target zone by shifting UTC by that
    // zone's offset. Returned Date's UTC fields represent the target zone's
    // local wall time, matching the original API of this helper.
    const now = new Date();
    const offsetMin = getTimezoneOffsetMinutes(now, timeZoneId);
    return new Date(now.getTime() + offsetMin * 60000);
};

/**
 * Converts a date/time string (already expressed in the site's wall-clock
 * time) to a Date object whose UTC fields hold those same wall-clock values.
 * The result is only meaningful when compared against another Date produced
 * with the same convention (see getCurrentTimeInTimezone). It is NOT a real
 * UTC instant and must not be sent to APIs or formatted as ISO.
 * @param dateStr - Date string in YYYY-MM-DD format
 * @param timeStr - Time string (24-hour "HH:MM" or 12-hour "h:mm AM/PM")
 */
export const createDateInTimezone = (
    dateStr: string,
    timeStr: string,
): Date => {
    const normalizedTime = normalizeTime(timeStr);
    if (!normalizedTime) {
        throw new Error(`Invalid time value: "${timeStr}"`);
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        throw new Error(`Invalid date value: "${dateStr}"`);
    }

    // Build the wall-clock instant as if it were UTC. We compare it against
    // getCurrentTimeInTimezone(...) which is also expressed as wall-clock in UTC
    // fields, so both sides live in the same reference frame.
    const [y, mo, d] = dateStr.split('-').map(n => Number.parseInt(n, 10));
    const [h, mi] = normalizedTime.split(':').map(n => Number.parseInt(n, 10));
    const wallClockMs = Date.UTC(y, mo - 1, d, h, mi, 0);
    const wallClockDate = new Date(wallClockMs);
    if (Number.isNaN(wallClockDate.getTime())) {
        throw new TypeError(`Failed to construct date from "${dateStr}" "${timeStr}"`);
    }
    return wallClockDate;
};

/**
 * Validates if a scheduled notification time is valid relative to site timezone
 * @param notificationDate - Notification date in YYYY-MM-DD format
 * @param notificationTime - Notification time in HH:MM format
 * @param shiftDate - Shift date in YYYY-MM-DD format
 * @param shiftTime - Shift start time in HH:MM format
 * @param timeZoneId - Site's IANA timezone identifier
 * @returns Object with isValid flag and optional error message
 */
export const validateNotificationTime = (
    notificationDate: string,
    notificationTime: string,
    shiftDate: string,
    shiftTime: string,
    timeZoneId: string
): { isValid: boolean; error?: string } => {
    if (!timeZoneId) {
        return { isValid: false, error: "Site timezone information is not available" };
    }

    try {
        // Get current time in site's timezone
        const currentTimeInSiteZone = getCurrentTimeInTimezone(timeZoneId);

        // Create notification datetime in site's timezone
        const notificationDateTime = createDateInTimezone(
            notificationDate,
            notificationTime,
        );

        // Create shift datetime in site's timezone
        const shiftDateTime = createDateInTimezone(
            shiftDate,
            shiftTime,
        );

        // Validate: notification must be after current time
        if (notificationDateTime < currentTimeInSiteZone) {
            return {
                isValid: false,
                error: "Notifications cannot be sent before current time"
            };
        }

        // Validate: notification must be before shift starts
        if (notificationDateTime > shiftDateTime) {
            return {
                isValid: false,
                error: "Notifications cannot be sent after the shift starts"
            };
        }

        return { isValid: true };
    } catch (error) {
        console.error("Error validating notification time:", error);
        return {
            isValid: false,
            error: "Error validating notification time. Please try again."
        };
    }
};

/**
 * Validates if a shift start time is in the future relative to site timezone
 * @param shiftDate - Shift date in YYYY-MM-DD format
 * @param shiftTime - Shift start time in HH:MM format
 * @param timeZoneId - Site's IANA timezone identifier
 * @returns Object with isValid flag and optional error message
 */
export const validateShiftStartTime = (
    shiftDate: string,
    shiftTime: string,
    timeZoneId: string
): { isValid: boolean; error?: string } => {
    if (!timeZoneId) {
        return { isValid: false, error: "Site timezone information is not available" };
    }

    try {
        // Get current time in site's timezone
        const currentTimeInSiteZone = getCurrentTimeInTimezone(timeZoneId);

        // Create shift datetime in site's timezone
        const shiftDateTime = createDateInTimezone(
            shiftDate,
            shiftTime,
        );

        // Validate: shift must be in the future
        if (shiftDateTime < currentTimeInSiteZone) {
            return {
                isValid: false,
                error: "Shift start must occur after current time"
            };
        }

        return { isValid: true };
    } catch (error) {
        console.error("Error validating shift start time:", error);
        return {
            isValid: false,
            error: "Error validating shift time. Please try again."
        };
    }
};

/**
 * Maps Windows timezone display names (and IANA ids) to a short abbreviation
 * suitable for compact UI labels like "Start Time (CST)". This is purely for
 * display — the original timeZoneId should still be passed to validation
 * helpers and to the API.
 *
 * Note: We return the "standard" abbreviation (e.g. CST) rather than trying to
 * detect DST (CDT). If you need DST-aware abbreviations, derive them from
 * Intl.DateTimeFormat with timeZoneName: 'short' against a resolved IANA id.
 */
const TIMEZONE_SHORT_LABEL_MAP: Record<string, string> = {
    // Windows display names
    'EASTERN STANDARD TIME': 'EST',
    'CENTRAL STANDARD TIME': 'CST',
    'MOUNTAIN STANDARD TIME': 'MST',
    'US MOUNTAIN STANDARD TIME': 'MST',
    'PACIFIC STANDARD TIME': 'PST',
    'ALASKAN STANDARD TIME': 'AKST',
    'HAWAIIAN STANDARD TIME': 'HST',
    'ATLANTIC STANDARD TIME': 'AST',

    // IANA ids (in case backend later switches)
    'AMERICA/NEW_YORK': 'EST',
    'AMERICA/CHICAGO': 'CST',
    'AMERICA/DENVER': 'MST',
    'AMERICA/PHOENIX': 'MST',
    'AMERICA/LOS_ANGELES': 'PST',
    'AMERICA/ANCHORAGE': 'AKST',
    'PACIFIC/HONOLULU': 'HST',
    'AMERICA/HALIFAX': 'AST',
};

/**
 * Returns a short display label for a timezone id (e.g. "CST").
 * Falls back to the original id if unknown.
 */
export const getShortTimeZoneLabel = (timeZoneId?: string | null): string => {
    if (!timeZoneId) return '';
    return TIMEZONE_SHORT_LABEL_MAP[timeZoneId.toUpperCase()] ?? timeZoneId;
};

/**
 * DST-aware variant: uses Intl to resolve the current abbreviation
 * (e.g. "CST" vs "CDT") based on the actual date. Falls back to the static
 * map above if Intl can't resolve.
 */
export const getShortTimeZoneLabelForDate = (
    timeZoneId?: string | null,
    date: Date = new Date(),
): string => {
    if (!timeZoneId) return '';
    try {
        const ianaId = resolveTimeZoneId(timeZoneId);
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: ianaId,
            timeZoneName: 'short',
        }).formatToParts(date);
        const tzPart = parts.find(p => p.type === 'timeZoneName')?.value;
        if (tzPart && /^[A-Z]{2,5}$/.test(tzPart)) return tzPart;
    } catch {
        // fall through
    }
    return getShortTimeZoneLabel(timeZoneId);
};